'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import {
  beginActivation,
  beginPasskeyLogin,
  finishActivation,
  finishPasskeyLogin,
} from '@/app/login/actions';
import { WELCOME_KEY } from '@/components/dashboard/DashboardSplash';
import Logo from './Logo';

export default function LoginForm() {
  const router = useRouter();
  const [key, setKey] = useState('');
  const [started, setStarted] = useState(false);
  const [ready, setReady] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const field = useRef(null);

  // Galaxy bloque la page à la compilation de ses shaders puis à sa première image :
  // l'entrée ne part qu'après deux images affichées, sinon le logo saute pour
  // rattraper son retard. Le délai de secours couvre les onglets où rAF est suspendu.
  useEffect(() => {
    let frame = 0;
    const start = () => setStarted(true);
    const fallback = window.setTimeout(start, 600);
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        window.clearTimeout(fallback);
        start();
      });
    });
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(fallback);
    };
  }, []);

  useEffect(() => {
    if (!started) return undefined;
    const id = window.setTimeout(() => setReady(true), 1000);
    return () => window.clearTimeout(id);
  }, [started]);

  useEffect(() => {
    if (ready) field.current?.focus();
  }, [ready]);

  async function run(mode, steps) {
    if (sending || !ready) return;
    if (!browserSupportsWebAuthn()) {
      setError('This browser does not support passkeys.');
      return;
    }
    setSending(mode);
    setError(null);
    try {
      const result = await steps();
      if (result?.error) {
        setError(result.error);
        setSending(false);
        return;
      }
      try {
        sessionStorage.setItem(WELCOME_KEY, '1');
      } catch {}
      router.replace('/dashboard');
    } catch (failure) {
      setError(
        failure?.name === 'NotAllowedError'
          ? 'The passkey prompt was closed. Try again.'
          : 'Something went wrong. Try again.',
      );
      setSending(false);
    }
  }

  function submit(event) {
    event.preventDefault();
    if (!key.trim()) return;
    run('key', async () => {
      const begin = await beginActivation(key);
      if (begin.error) return begin;
      // Clé sans passkey : la session est déjà ouverte côté serveur, pas de WebAuthn.
      if (begin.ok) return begin;
      return finishActivation(await startRegistration({ optionsJSON: begin.options }));
    });
  }

  function signInWithPasskey() {
    run('passkey', async () => {
      const begin = await beginPasskeyLogin();
      if (begin.error) return begin;
      return finishPasskeyLogin(await startAuthentication({ optionsJSON: begin.options }));
    });
  }

  const filled = key.trim().length > 0;

  return (
    <div className={`login${started ? ' login--started' : ''}`}>
      <form
        className="login__card"
        onSubmit={submit}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="login__body">
          <Logo />
          <h1 className="login__name">Argos</h1>
          <div className={`login__access${ready ? ' login__access--ready' : ''}`}>
            <div className="login__row">
              <input
                ref={field}
                className="login__field"
                type="text"
                value={key}
                onChange={(event) => setKey(event.target.value)}
                placeholder="Enter your access key"
                autoComplete="off"
                spellCheck={false}
                disabled={!ready}
                aria-label="Access key"
              />
            </div>
            <button
              className="login__submit"
              type="submit"
              disabled={!filled || Boolean(sending) || !ready}
            >
              {sending === 'key' ? <span className="login__spinner" aria-hidden="true" /> : 'Continue'}
            </button>
            <button
              className="login__passkey"
              type="button"
              onClick={signInWithPasskey}
              disabled={Boolean(sending) || !ready}
            >
              {sending === 'passkey' ? 'Waiting for your passkey…' : 'Already activated? Sign in with your passkey'}
            </button>
            {error ? (
              <p className="login__error" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        </div>
      </form>
    </div>
  );
}
